import * as React from 'react';
import { Button } from './button';

// ============================================================================
// Error Boundary Types
// ============================================================================

interface ErrorBoundaryProps {
  /** Custom fallback UI to render when an error is caught */
  children: React.ReactNode;
  /** Optional section name for logging/display */
  section?: string;
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Whether to show a compact fallback (for smaller panels) */
  compact?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ============================================================================
// ErrorBoundary Component
// ============================================================================

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const section = this.props.section || 'Unknown';
    // Log to console.error (kept in production for monitoring)
    console.error(`[ErrorBoundary:${section}] Component error:`, error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.compact) {
        return (
          <CompactErrorFallback
            error={this.state.error}
            section={this.props.section}
            onRetry={this.handleRetry}
          />
        );
      }
      return (
        <DefaultErrorFallback
          error={this.state.error}
          section={this.props.section}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// Default Error Fallback (full-size sections)
// ============================================================================

function DefaultErrorFallback({
  error,
  section,
  onRetry,
}: {
  error: Error | null;
  section?: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center p-8 text-center min-h-[200px]">
      <div className="mb-4">
        <svg
          className="h-12 w-12 text-destructive/60 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
      </div>
      <h2 className="text-lg font-semibold text-foreground mb-2">
        Something went wrong
      </h2>
      {section && (
        <p className="text-sm text-muted-foreground mb-1">
          Error in: {section}
        </p>
      )}
      <p className="text-sm text-muted-foreground max-w-md mb-4">
        {error?.message || 'An unexpected error occurred. Please try again.'}
      </p>
      <Button onClick={onRetry} variant="default" size="sm">
        Try Again
      </Button>
    </div>
  );
}

// ============================================================================
// Compact Error Fallback (for smaller panels/sidebars)
// ============================================================================

function CompactErrorFallback({
  error,
  section,
  onRetry,
}: {
  error: Error | null;
  section?: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex items-center gap-3 p-4 border border-destructive/20 rounded-lg bg-destructive/5 m-2">
      <svg
        className="h-5 w-5 text-destructive flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
        />
      </svg>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {section ? `${section} error` : 'Something went wrong'}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {error?.message || 'An unexpected error occurred'}
        </p>
      </div>
      <Button onClick={onRetry} variant="outline" size="sm">
        Retry
      </Button>
    </div>
  );
}

// ============================================================================
// App-Level Error Fallback (for the top-level boundary)
// ============================================================================

export function AppErrorFallback({
  error,
  onRetry,
}: {
  error: Error | null;
  onRetry: () => void;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center max-w-lg">
        <div className="mb-6">
          <svg
            className="h-16 w-16 text-destructive/60 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-semibold text-foreground mb-3">
          Application Error
        </h1>
        <p className="text-muted-foreground mb-2">
          Aerotage encountered an unexpected error and needs to restart.
        </p>
        <p className="text-sm text-muted-foreground mb-6 font-mono bg-muted rounded-lg p-3">
          {error?.message || 'Unknown error'}
        </p>
        <div className="flex gap-3 justify-center">
          <Button onClick={onRetry} variant="default">
            Try Again
          </Button>
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
          >
            Reload Page
          </Button>
        </div>
      </div>
    </div>
  );
}
